// 04 / YOUR DETAILS — name, email, phone, and delivery address. The address
// is what unlocks the tax + total in the Summary section below, so we keep
// it all in one block.

import { useState } from "react";

import { SectionFrame } from "./SectionFrame";
import { api } from "../../api";
import { useConfigurator } from "../../store";
import type { CustomerInfo } from "../../store";

export function DetailsSection() {
  const { state, dispatch } = useConfigurator();
  const { customer, employee } = state;
  // Employee mode: entering an email and tabbing out pulls the rest of the
  // contact's details from HubSpot (email is unique there, so ≤1 match).
  const [lookup, setLookup] = useState<"idle" | "loading" | "found" | "none">(
    "idle",
  );

  async function lookupContact() {
    const email = customer.email.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!employee.active || !employee.token || !valid) return;
    setLookup("loading");
    try {
      const res = await api.employeeContactLookup(employee.token, email);
      if (res.found && res.contact) {
        const c = res.contact;
        // Fill only the fields HubSpot actually has — never clobber a value
        // the rep already typed with a blank.
        const patch: Partial<CustomerInfo> = {};
        if (c.first_name) patch.firstName = c.first_name;
        if (c.last_name) patch.lastName = c.last_name;
        if (c.phone) patch.phone = c.phone;
        if (c.address) patch.address = c.address;
        if (c.city) patch.city = c.city;
        if (c.state) patch.state = c.state;
        if (c.zip) patch.zip = c.zip;
        dispatch({ type: "customer/update", patch });
        setLookup("found");
      } else {
        setLookup("none");
      }
    } catch {
      setLookup("none");
    }
  }

  return (
    <SectionFrame
      number={4}
      label="Your Details"
      title="Where will we set it up?"
      meta="* Required"
    >
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="First name *"
          value={customer.firstName}
          onChange={(v) =>
            dispatch({ type: "customer/update", patch: { firstName: v } })
          }
          autoComplete="given-name"
        />
        <Field
          label="Last name *"
          value={customer.lastName}
          onChange={(v) =>
            dispatch({ type: "customer/update", patch: { lastName: v } })
          }
          autoComplete="family-name"
        />
        <Field
          label="Email *"
          type="email"
          value={customer.email}
          onChange={(v) =>
            dispatch({ type: "customer/update", patch: { email: v } })
          }
          onBlur={employee.active ? lookupContact : undefined}
          autoComplete="email"
          className="col-span-2"
        />
        {employee.active && (
          <p className="col-span-2 -mt-1.5 font-sans text-[13px] text-steel">
            {lookup === "loading"
              ? "Looking up in HubSpot…"
              : lookup === "found"
                ? "✓ Prefilled from HubSpot — edit anything as needed."
                : lookup === "none"
                  ? "No HubSpot contact with that email — fill the rest in manually."
                  : "Tip: enter the customer's email and tab out to prefill from HubSpot."}
          </p>
        )}
        <Field
          label="Phone"
          type="tel"
          value={customer.phone}
          onChange={(v) =>
            dispatch({ type: "customer/update", patch: { phone: v } })
          }
          autoComplete="tel"
          className="col-span-2"
        />

        <div className="col-span-2 mt-2 flex items-center gap-2 border-t border-dashed border-line pt-3">
          <span aria-hidden className="h-2 w-2 bg-amber" />
          <span className="font-sans text-[14px] font-bold uppercase tracking-tag text-ink">
            Delivery Address
          </span>
        </div>

        <Field
          label="Street address *"
          value={customer.address}
          onChange={(v) =>
            dispatch({ type: "customer/update", patch: { address: v } })
          }
          autoComplete="address-line1"
          className="col-span-2"
        />
        <Field
          label="City *"
          value={customer.city}
          onChange={(v) =>
            dispatch({ type: "customer/update", patch: { city: v } })
          }
          autoComplete="address-level2"
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="State / Province *"
            value={customer.state}
            onChange={(v) =>
              dispatch({
                type: "customer/update",
                // Uppercase (HubSpot wants an uppercase code) but don't
                // truncate: a customer may type a full name ("Illinois") or a
                // 2-letter code ("IL"). The backend normalizes either to the
                // 2-letter code HubSpot + TaxJar require. Cap length just to
                // keep the input sane.
                patch: { state: v.toUpperCase().slice(0, 24) },
              })
            }
            autoComplete="address-level1"
          />
          <Field
            label="ZIP / Postal Code *"
            value={customer.zip}
            onChange={(v) =>
              dispatch({
                type: "customer/update",
                // Accept letters + digits + spaces + dashes (Canadian
                // postal codes are alphanumeric with an optional space).
                // Trim to 10 chars max to keep input sane.
                patch: { zip: v.toUpperCase().slice(0, 10) },
              })
            }
            autoComplete="postal-code"
          />
        </div>
      </div>
    </SectionFrame>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: "text" | "email" | "tel";
  inputMode?: "text" | "numeric" | "tel" | "email";
  autoComplete?: string;
  className?: string;
}

function Field({
  label,
  value,
  onChange,
  onBlur,
  type = "text",
  inputMode,
  autoComplete,
  className = "",
}: FieldProps) {
  return (
    <label className={"flex flex-col gap-1.5 " + className}>
      {/* 14px (was 9px — 9px × 1.6 = ~14.4px per user request to bump
          60%); text-ink instead of text-steel (19.6:1 contrast vs the
          old 4.5:1). Reads cleanly without a magnifier. */}
      <span className="font-sans text-sm font-bold uppercase tracking-tag text-ink">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        inputMode={inputMode}
        autoComplete={autoComplete}
        className="rounded-card border border-line bg-white px-3 py-2.5 font-sans text-base text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-amber/30"
      />
    </label>
  );
}
